import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Button, Divider, Typography } from "@mui/material";

import APP_ROUTES from "../../../../../routers/routeConstants";

import { useTopServiceProviders } from "../../../api/queries/useTopServiceProviders";

import GoHireSlider from "../../../components/GoHireSlider";
import EmptyServiceProvidersFigure from "../../../assets/images/empty-service-providers.png";
import { ServiceProviderCard, ServiceProviderCardLoader } from "../../../components/ServiceProviderCard";
import { ArrowRightButtonIcon, TopServiceProvidersTitleIcon } from "../../../assets/icons/go-hire-icons";

const TopServiceProviders = () => {
  const { t } = useTranslation("goService", { keyPrefix: "goService" });
  const navigate = useNavigate();

  const {
    data: topServiceProviders,
    isLoading: isTopServiceProvidersLoading,
    isError: isTopServiceProvidersError,
  } = useTopServiceProviders({
    limit: 20,
    offset: 0,
  });

  const handleSeeAllProviders = () => {
    navigate(APP_ROUTES.TRENDING_PROVIDERS);
  };

  if ((!isTopServiceProvidersLoading && topServiceProviders?.items.length === 0) || isTopServiceProvidersError) {
    return <EmptyServiceProviders />;
  }

  return (
    <Box sx={styles.container}>
      <Box sx={styles.header}>
        <Typography sx={styles.title}>
          <TopServiceProvidersTitleIcon />
          {t("topServiceProviders")}
        </Typography>
        <Button
          onClick={handleSeeAllProviders}
          endIcon={<ArrowRightButtonIcon />}
          variant="text-md"
          sx={styles.seeAllButton}
        >
          {t("seeAllProfessionals")}
        </Button>
      </Box>
      <Divider sx={styles.divider} />
      <GoHireSlider
        items={topServiceProviders?.items.map((user) => <ServiceProviderCard key={user.id} user={user} />) || []}
        isLoading={isTopServiceProvidersLoading}
        loadingComponent={<ServiceProviderCardLoader />}
        loadingCount={10}
        numberOfSlidesPerView={4}
      />
    </Box>
  );
};

export default TopServiceProviders;

const EmptyServiceProviders = () => {
  const { t } = useTranslation("goService", { keyPrefix: "goService" });
  return (
    <Box sx={styles.emptyWrapper}>
      <Box component="img" src={EmptyServiceProvidersFigure} sx={styles.emptyImage} />
      <Typography sx={styles.emptyText}>{t("emptyServiceProviders")}</Typography>
    </Box>
  );
};

const styles = {
  container: {
    padding: "20px 24px",
    borderRadius: "8px",
    border: "1px solid #EAECF0",
    background: "#FFF",
    display: "flex",
    flexDirection: "column",
    gap: "24px",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  title: {
    fontFamily: "Montserrat",
    color: "var(--black-100, #1C1C1C)",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "28px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  divider: {
    margin: "0 -24px",
    borderColor: "#EAECF0",
  },
  seeAllButton: {
    padding: 0,
    background: "transparent !important",
  },
  emptyWrapper: {
    padding: "20px 24px",
    borderRadius: "8px",
    border: "1px solid #EAECF0",
    background: "#FFF",
    display: "flex",
    flexDirection: "column",
    gap: "24px",
    justifyContent: "center",
    alignItems: "center",
  } as const,

  emptyImage: {
    width: "300px",
    height: "225px",
  } as const,

  emptyText: {
    color: "var(--Gray-Gray-700, #5E6278)",
    textAlign: "center",
    fontFamily: "Montserrat",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "150%",
  } as const,
};
